import { useApi } from 'api/useApi';
import { ENV_VARS } from 'app/config/environment-vars';

const PROJECT_TEAM_CONFIGURATIONS_ERROR_KEY = 'ProjectTeamConfigurationsApi';

export const useProjectTeamApi = () => {
  const baseURL = `${ENV_VARS.META_URL}/api/projectTeam`;
  const fetchFunction = useApi(baseURL, PROJECT_TEAM_CONFIGURATIONS_ERROR_KEY);

  const getProjectTeams = () => fetchFunction<ProjectTeamConfiguration[]>('');

  /** The one team whose members work on `projectId`, with only that project's members. */
  const getProjectTeamByProjectId = (projectId: string) => fetchFunction<ProjectTeamOfProject>(`/project/${projectId}`);

  const getProjectTeamsByTemplateId = (templateId: string) =>
    fetchFunction<ProjectTeamConfiguration[]>(`/byTemplate/${templateId}`);

  const getProjectTeamById = (projectTeamId: string) => fetchFunction<ProjectTeamConfiguration>(`/${projectTeamId}`);

  const createProjectTeam = (name?: string, description?: string) =>
    fetchFunction<ProjectTeamConfiguration>('', {
      method: 'POST',
      body: JSON.stringify({ name, description }),
      headers: { 'Content-Type': 'application/json' }
    });

  const updateProjectTeamName = (projectTeamId: string, newName: string) =>
    fetchFunction<ProjectTeamConfiguration>('/name', {
      method: 'PATCH',
      body: JSON.stringify({ name: newName, projectTeamId }),
      headers: { 'Content-Type': 'application/json' }
    });

  const updateProjectTeamDescription = (projectTeamId: string, newDescription: string) =>
    fetchFunction<ProjectTeamConfiguration>('/description', {
      method: 'PATCH',
      body: JSON.stringify({ description: newDescription, projectTeamId }),
      headers: { 'Content-Type': 'application/json' }
    });

  const createRole = (projectTeamId: string, projectTeamMappingId: string) =>
    fetchFunction<ProjectRole>('/role', {
      method: 'POST',
      body: JSON.stringify({ projectTeamId, projectTeamMappingId }),
      headers: { 'Content-Type': 'application/json' }
    });

  const updateProjectTeamRoleName = (projectTeamId: string, roleId: string, newName: string) =>
    fetchFunction<ProjectRole>('/role/name', {
      method: 'PATCH',
      body: JSON.stringify({ name: newName, projectTeamId, roleId }),
      headers: { 'Content-Type': 'application/json' }
    });

  const updateProjectTeamRoleDescription = (projectTeamId: string, roleId: string, newDescription: string) =>
    fetchFunction<ProjectRole>('/role/description', {
      method: 'PATCH',
      body: JSON.stringify({
        description: newDescription,
        projectTeamId,
        roleId
      }),
      headers: { 'Content-Type': 'application/json' }
    });

  const deleteProjectTeamRole = (projectTeamId: string, roleId: string) =>
    fetchFunction<ProjectRole>(`/${projectTeamId}/role/${roleId}`, {
      method: 'DELETE'
    });

  return {
    getProjectTeams,
    getProjectTeamByProjectId,
    getProjectTeamsByTemplateId,
    getProjectTeamById,
    createProjectTeam,
    updateProjectTeamName,
    updateProjectTeamDescription,
    createRole,
    updateProjectTeamRoleName,
    updateProjectTeamRoleDescription,
    deleteProjectTeamRole
  };
};

export interface CreateProjectTeamModel {
  name: string;
  description: string;
}

export interface UpdateProjectTeamNameModel {
  name: string;
  projectTeamId: string;
}

export interface UpdateProjectTeamDescriptionModel {
  description: string;
  projectTeamId: string;
}

export interface ProjectTeamConfiguration {
  id: string;
  name: string;
  description: string;
  members: ProjectTeamMember[];
  roles: ProjectRole[];
}

/**
 * `GET /projectTeam/project/{projectId}`. A project nobody has been assigned to yet still answers
 * success, with an empty team — no id, no roles — rather than a 404, hence every field is optional.
 */
export type ProjectTeamOfProject = Partial<ProjectTeamConfiguration>;

export interface ProjectRole {
  id: string;
  name: string;
  description: string;
}

export interface ProjectTeamMember {
  email: string;
  id: string;
  projectId: string;
  roles: string[];
}

export interface ProjectTeamMapping {
  projectTeamMappingId: string;
  projectTeamConfigurationId: string; // TODO: Update backend to use 'id' instead
  templateId: string;
  projectTeamRoleMappings: ProjectTeamRoleMapping[];
}

export interface ProjectTeamRoleMapping {
  projectTeamRoleMappingId: string;
  roleId: string;
  moduleAssignments: ModuleAssignment[];
  overrideAssignments: PropertyOverrideAssignment[];
  relationshipOverrideAssignments: RelationshipOverrideAssignment[];

  projectTeamMappingId: string;
}

export enum EOverrideAccessRights {
  view = 0,
  edit = 1
}

export interface ModuleAssignment {
  moduleAssignmentId: string;
  templateModuleId: string;
  accessRights: EOverrideAccessRights;
}

export interface PropertyOverrideAssignment {
  propertyOverrideAssignmentId: string;
  templateNodePropertyId: string;
  accessRights: EOverrideAccessRights;
}

export interface RelationshipOverrideAssignment {
  relationshipOverrideAssignmentId: string;
  templateRelationId: string;
  accessRights: EOverrideAccessRights;
}

export interface UpdateProjectTeamRoleNameModel {
  newName: string;
  name: string;
  projectTeamId: string;
  roleId: string;
}

export interface UpdateProjectTeamRoleDescriptionModel {
  newDescription: string;
  description: string;
  projectTeamId: string;
  roleId: string;
}

export interface CreateProjectTeamRoleModel {
  projectTeamId: string;
  name: string;
  description: string;
  projectTeamMappingId: string;
}
